/**
 * AI Discussion Stage Component
 * Stage where user discusses their sourcing decisions with AI mentor via voice
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import VoiceInput from '../VoiceInput';
import {
    MessageSquare,
    Target,
    Eye,
    Building2,
    Users,
    Search,
    CheckCircle,
    Loader2,
    Award,
    Brain,
    Mic,
    Volume2
} from 'lucide-react';

const AiDiscussionStage = ({
    messages,
    input,
    setInput,
    onSend,
    aiLoading,
    messagesEndRef,
    onComplete,
    loading,
    api,
    session
}) => {
    const [activePrompt, setActivePrompt] = useState(null);
    const [conversationStarted, setConversationStarted] = useState(false);

    // Track if conversation has started (either from loaded messages or new messages)
    useEffect(() => {
        if (messages && messages.length > 0) {
            setConversationStarted(true);
        }
    }, [messages]);

    // Guided discussion prompts
    const discussionPrompts = [
        {
            id: 'overall_approach',
            label: 'Discuss My Overall Approach',
            icon: Target,
            prompt: 'I want to discuss my overall approach to evaluating these candidates. What did I do well and what could I improve?',
            color: 'text-blue-500 bg-blue-500/10'
        },
        {
            id: 'missed_signals',
            label: 'What Did I Miss?',
            icon: Eye,
            prompt: 'Were there any important signals or red flags in the candidate profiles that I may have missed in my evaluations?',
            color: 'text-amber-500 bg-amber-500/10'
        },
        {
            id: 'industry_context',
            label: 'Industry Context',
            icon: Building2,
            prompt: 'Can you help me understand the industry context better? What types of companies and backgrounds should I prioritize for this role?',
            color: 'text-emerald-500 bg-emerald-500/10'
        },
        {
            id: 'specific_candidate',
            label: 'Discuss a Specific Candidate',
            icon: Users,
            prompt: 'I want to discuss one of the candidates I evaluated. Let me explain my thinking and get your feedback...',
            color: 'text-purple-500 bg-purple-500/10'
        },
        {
            id: 'search_strategy',
            label: 'Improve My Search Strategy',
            icon: Search,
            prompt: 'How could I improve my Boolean search string to find better candidates for this role?',
            color: 'text-cyan-500 bg-cyan-500/10'
        }
    ];

    const handlePromptSelect = (prompt) => {
        setActivePrompt(prompt.id);
    };

    const handleVoiceSend = (transcript) => {
        if (transcript && transcript.trim()) {
            // Combine with active prompt context if selected
            let fullMessage = transcript;
            if (activePrompt) {
                const selectedPrompt = discussionPrompts.find(p => p.id === activePrompt);
                if (selectedPrompt && !conversationStarted) {
                    fullMessage = `${selectedPrompt.prompt}\n\nMy thoughts: ${transcript}`;
                }
            }
            
            // Mark conversation as started
            setConversationStarted(true);
            
            // Set input and call onSend directly with the message
            // Use callback pattern to ensure state is updated
            setInput(fullMessage);
            
            // Call onSend with a slight delay to ensure input state is set
            // But also pass the message directly as a fallback
            setTimeout(() => {
                if (onSend) {
                    onSend(fullMessage);  // Pass message directly
                }
                setActivePrompt(null);
            }, 150);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-purple-500" />
                    AI Mentor Discussion
                </CardTitle>
                <CardDescription>
                    Speak with your AI hiring manager mentor. Discuss your decisions and get feedback.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Voice-First Instructions */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-purple-500/20">
                            <Mic className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-purple-600">Voice Conversation</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                                Select a topic below, then speak your thoughts. Practice explaining your sourcing decisions verbally!
                            </p>
                        </div>
                    </div>
                </div>

                {/* Guided Prompts - Only show if no conversation started yet */}
                {!conversationStarted && (
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Choose a discussion topic:</Label>
                        <div className="grid gap-2">
                            {discussionPrompts.map((prompt) => {
                                const IconComponent = prompt.icon;
                                return (
                                    <button
                                        key={prompt.id}
                                        onClick={() => handlePromptSelect(prompt)}
                                        className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                            activePrompt === prompt.id 
                                                ? 'ring-2 ring-primary border-primary' 
                                                : 'hover:bg-muted/50'
                                        }`}
                                    >
                                        <div className={`p-2 rounded-lg ${prompt.color}`}>
                                            <IconComponent className="h-4 w-4" />
                                        </div>
                                        <span className="font-medium text-sm">{prompt.label}</span>
                                        {activePrompt === prompt.id && (
                                            <CheckCircle className="h-4 w-4 text-primary ml-auto" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Messages */}
                <ScrollArea className="h-[300px] rounded-lg border p-4">
                    <div className="space-y-4">
                        {!conversationStarted && !activePrompt && (
                            <div className="text-center py-8 text-muted-foreground">
                                <Brain className="h-12 w-12 mx-auto mb-4 opacity-30" />
                                <p>Select a topic above to start the discussion.</p>
                                <p className="text-sm mt-1">Practice articulating your sourcing decisions!</p>
                            </div>
                        )}

                        {!conversationStarted && activePrompt && (
                            <div className="text-center py-4 text-muted-foreground">
                                <Mic className="h-8 w-8 mx-auto mb-2 text-primary animate-pulse" />
                                <p className="text-sm">Now speak your thoughts on this topic...</p>
                            </div>
                        )}
                        
                        {messages.map((msg, idx) => (
                            <div 
                                key={idx}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[80%] p-3 rounded-xl ${
                                    msg.role === 'user' 
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted'
                                }`}>
                                    {msg.role === 'user' && (
                                        <div className="flex items-center gap-1 mb-1 opacity-70">
                                            <Mic className="h-3 w-3" />
                                            <span className="text-xs">You said:</span>
                                        </div>
                                    )}
                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                </div>
                            </div>
                        ))}
                        
                        {aiLoading && (
                            <div className="flex justify-start">
                                <div className="bg-muted p-3 rounded-xl flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-sm text-muted-foreground">AI mentor is thinking...</span>
                                </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>
                </ScrollArea>

                {/* Voice Input for Discussion - Only show when conversation started or topic selected */}
                {(conversationStarted || activePrompt) && (
                    <div className="p-4 rounded-xl border bg-muted/20">
                        <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                            <Volume2 className="h-4 w-4" />
                            {!conversationStarted ? 'Speak your thoughts on the selected topic' : 'Continue the conversation'}
                        </Label>
                        <VoiceInput
                            api={api}
                            value={input}
                            onTranscript={handleVoiceSend}
                            placeholder={!conversationStarted ? "Speak your thoughts on this topic..." : "Continue the discussion with the AI mentor..."}
                            showTranscript={false}
                            disabled={false}
                        />
                    </div>
                )}

                {/* Complete Session - Only after some discussion */}
                {messages.length >= 2 && (
                    <div className="pt-4 border-t">
                        <Button 
                            onClick={onComplete} 
                            disabled={loading}
                            className="w-full"
                            data-testid="complete-session-btn"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <>
                                    <Award className="h-4 w-4 mr-2" />
                                    Complete Session & View Results
                                </>
                            )}
                        </Button>
                        <p className="text-xs text-center text-muted-foreground mt-2">
                            You can continue the discussion or complete your session now.
                        </p>
                    </div>
                )}

                {/* Prompt to complete if no discussion yet */}
                {messages.length < 2 && (
                    <p className="text-xs text-center text-muted-foreground">
                        Have at least one exchange with the AI mentor before completing the session.
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

export default AiDiscussionStage;
